import React from 'react';
import moment from 'moment';
import { Alert, Button } from '@mui/material';

import {
  LiveAssistContextDetails as Details,
  LiveAssistConversationItem,
  LiveAssistTicketItem,
} from 'components/Alpha/LiveAssist/liveAssistContract';
import { useLiveAssistStyles } from 'components/Alpha/LiveAssist/liveAssistStyles';

export const OPEN_TICKET_LABEL = 'Open the ticket';

export const unavailableText = (sections: string[]): string =>
  `Some details couldn't load: ${sections.join(', ')}.`;

const DIRECTIONS: Record<string, string> = { inbound: 'Incoming', outbound: 'Outgoing' };

const when = (at?: string): string | undefined => {
  if (at === undefined) return undefined;
  const parsed = moment.utc(at);
  return parsed.isValid() ? parsed.local().format('D MMM, h:mm a') : undefined;
};

const joined = (parts: Array<string | undefined>): string =>
  parts.filter((part): part is string => part !== undefined && part !== '').join(' · ');

const ticketLabel = (item: LiveAssistTicketItem): string =>
  joined([item.number !== undefined ? `#${item.number}` : undefined, item.title]);

const TicketLine: React.FC<{ item: LiveAssistTicketItem }> = ({ item }) => {
  const classes = useLiveAssistStyles();
  const meta = joined([item.status, item.priority, item.age]);
  return (
    <li className={classes.detailItem}>
      {item.url !== undefined ? (
        <a className={classes.sourceLink} href={item.url} target="_blank" rel="noopener noreferrer">
          {ticketLabel(item)}
        </a>
      ) : (
        <span className={classes.detailText}>{ticketLabel(item)}</span>
      )}
      {meta !== '' && <span className={classes.detailMeta}>{meta}</span>}
    </li>
  );
};

const ConversationLine: React.FC<{ item: LiveAssistConversationItem }> = ({ item }) => {
  const classes = useLiveAssistStyles();
  const direction =
    item.direction !== undefined ? DIRECTIONS[item.direction.toLowerCase()] : undefined;
  return (
    <li className={classes.detailItem}>
      <span className={classes.detailText}>{item.title ?? 'Call'}</span>
      <span className={classes.detailMeta}>{joined([when(item.at), direction])}</span>
    </li>
  );
};

const Section: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({
  title,
  count,
  children,
}) => {
  const classes = useLiveAssistStyles();
  if (count === 0) return null;
  return (
    <details className={classes.section}>
      <summary className={classes.sectionSummary}>{`${title} (${count})`}</summary>
      <ul className={classes.detailList}>{children}</ul>
    </details>
  );
};

interface Props {
  details: Details;
  onRetry: () => void;
}

const LiveAssistContextDetails: React.FC<Props> = ({ details, onRetry }) => {
  const classes = useLiveAssistStyles();
  return (
    <div className={classes.details}>
      {details.ticket?.url !== undefined && (
        <a
          className={classes.sourceLink}
          href={details.ticket.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {OPEN_TICKET_LABEL}
        </a>
      )}
      {details.contactPhones.length > 0 && (
        <p className={classes.detailLine}>
          <span className={classes.detailLabel}>Phone</span>
          <span className={classes.detailText}>{details.contactPhones.join(', ')}</span>
        </p>
      )}
      {details.notices.map(notice => (
        <p key={notice} className={classes.detailNotice}>
          {notice}
        </p>
      ))}
      {details.unavailable.length > 0 && (
        <Alert
          severity="warning"
          className={classes.notice}
          action={
            <Button size="small" className={classes.noticeButton} onClick={onRetry}>
              Try again
            </Button>
          }
        >
          {unavailableText(details.unavailable)}
        </Alert>
      )}
      <Section title="Open tickets" count={details.openTickets.length}>
        {details.openTickets.map((item, index) => (
          <TicketLine key={`${item.number ?? item.title ?? ''}-${index}`} item={item} />
        ))}
      </Section>
      <Section title="Closed in the last 90 days" count={details.recentClosedTickets.length}>
        {details.recentClosedTickets.map((item, index) => (
          <TicketLine key={`${item.number ?? item.title ?? ''}-${index}`} item={item} />
        ))}
      </Section>
      <Section title="Recent calls" count={details.recentConversations.length}>
        {details.recentConversations.map((item, index) => (
          <ConversationLine key={`${item.at ?? ''}-${index}`} item={item} />
        ))}
      </Section>
    </div>
  );
};

export default LiveAssistContextDetails;
